// A CUSTOM PART CATEGORY, WITHOUT SELECTING ANYTHING FIRST.
//
// Owner, 2026-10-02: "allow in the right panel, to add a custom category
// for tracking, that counts assembly parts, from a dropdown list of all
// tracked categories, until we get to the assembly parts, and use their
// quantities and other available data."
//
// WHAT WAS ALREADY THERE, and this deliberately does not duplicate any of
// it: models/derivedCategory is the definition, GET /assembly/groups
// answers the part groups of a category, POST /derived-categories creates
// one and prices it with dryRun, and AssemblyGroups renders the groups
// with the create dialog. All of it shipped 2026-09-24 from the same owner
// request. The ONLY thing missing was the way in: AssemblyGroups is fed the
// CURRENT SELECTION, so you had to select elements in the model before you
// could see their parts.
//
// SO THIS IS AN ENTRY POINT, NOT A FEATURE. It asks the same endpoint with
// a category instead of a list of ids, and hands the answer to the same
// component. If this file ever grows a definition, a measure or a sum of
// its own, it has gone wrong.
//
// NAME FIRST, THEN TICKS (owner, 2026-10-04: "shouldn't the user be doing
// that from the popup panel itself by giving a name first, then using a tree
// selection checkbox like the model elements to check box what subpart from
// what category they need tracked?").
//
// So the dialog creates. It carries a name field and a checkbox tree whose
// roots are the tracked categories and whose leaves are their part families
// and types, and SEVERAL TICKS MAKE ONE CATEGORY - which is why
// models/derivedCategory now holds `parts: [{category, family, type}]`
// instead of one triple. Before that, his beam clamps had to be three
// separate categories because U-Bolt, Haydon and CON are three part
// families and a definition could only name one.
//
// TICKING A CATEGORY MEANS EVERY PART UNDER IT, INCLUDING ONES THAT ARRIVE
// LATER. That is the answer to "they dynamically get updated from incoming
// commits": a definition is a definition, not a copy - every read resolves
// the live commit - so a new publish plus its quantity build changes the
// numbers with nobody re-saving anything. A tick on a NAMED family keeps
// naming that family, which is a choice and not a bug; a tick on the
// category absorbs new families too.
//
// 51 NODES, MEASURED. On SpaceX BA03 the whole commit's 1,214,057 parts
// collapse to 51 distinct (part category, part family) groups - smaller
// than the category tree this page already draws. That measurement is why
// this is a dropdown and not an on-demand tree.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Input, Spin, Tree } from "antd";
import AssemblyGroups from "./AssemblyGroups";
import { PART_CATEGORY_TOTALS_RULE, PART_CATEGORY_OVERLAP_NOTE } from "./partCategoryRule";

const T = { line: "#E5E7EB", muted: "#6B7280", ink: "#111827" };

export default function PartCategoryPicker({
  projectId,
  // The tracked categories, as the page already knows them. Passed in
  // rather than fetched: the page has asked for this tree already, and
  // taking its answer is what keeps one list in one place.
  categories = [],
  documentIds = "",
  loadGroups = null,     // (params) => Promise<payload>
  onCreate = null,       // (definition) => Promise<void>
  onPreview = null,      // (definition) => Promise<preview>
  statusMode = "",
  canEdit = false,
}) {
  const [category, setCategory] = useState("");
  const [family, setFamily] = useState("");
  const [type, setType] = useState("");
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // FAMILY AND TYPE ARE NARROWINGS OF THE PARENT, so changing the
  // category has to clear them. Leaving a stale family behind would ask
  // the server for "Hangers / a family that only exists under Conduits"
  // and get an empty answer that reads as "this category has no parts".
  const pickCategory = (v) => { setCategory(v || ""); setFamily(""); setType(""); setPayload(null); };

  const ask = useCallback(async () => {
    if (!projectId || !category || !loadGroups) { setPayload(null); return; }
    setLoading(true);
    setErr("");
    try {
      const r = await loadGroups({
        category, family: family || undefined, type: type || undefined,
        documentIds: documentIds || undefined,
      });
      setPayload(r || null);
    } catch (e) {
      // A FAILED READ IS NOT AN EMPTY CATEGORY. Setting the payload to an
      // empty answer here would print "no assembly parts", which is a
      // statement about the model, from a network error.
      setErr(e.message || "Could not read the assembly parts for that category");
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, category, family, type, documentIds, loadGroups]);

  useEffect(() => { ask(); }, [ask]);

  const catOptions = useMemo(
    () => (categories || [])
      .filter(Boolean)
      .map((c) => (typeof c === "string" ? { value: c, label: c } : { value: c.name || c.category, label: `${c.name || c.category}${c.count ? ` · ${c.count.toLocaleString()}` : ""}` }))
      .filter((o) => o.value),
    [categories],
  );

  const groupCount = ((payload && payload.groups) || []).length;

  // THE TRACKED CATEGORIES AS CHECKBOXES, WITH THEIR LEAVES (owner,
  // 2026-10-03: "keep custom category working as it is, just show the tracked
  // categories checkboxes and their leafs so the user can pick sub parts from
  // them").
  //
  // Nothing below this changes: ticking a node sets the same category /
  // family / type the three dropdowns used to set, the same loadGroups call
  // answers, and AssemblyGroups shows and creates exactly as before. This is
  // the SOURCE picker's presentation and nothing else.
  //
  // ONE TICK AT A TIME, because a definition has one source
  // (models/derivedCategory: source.category / family / type, single values).
  // Ticking a second node moves the tick rather than adding to it - checkable
  // with checkStrictly, and onCheck keeps the node that was just touched. A
  // multi-tick that silently used only one of them would be the worse lie.
  //
  // THE LEAVES COME FROM THE ANSWER WE ALREADY HAVE, loaded per category on
  // expand - the part families, then their types. Measured on BA03: the whole
  // commit's 1.2M parts collapse to 51 distinct (part category, part family)
  // groups, so a category's leaves are a short list, not a second tree.
  const [leavesBy, setLeavesBy] = useState({});   // category -> [{pf, pts:[]}]
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [creating, setCreating] = useState(false);
  // EVERY TICK, not one. Keyed the same way the tree keys its nodes, so the
  // set IS the checkedKeys and there is no second notion of what is ticked.
  const [ticks, setTicks] = useState([]);
  // CONTROLLED, because a tick has to OPEN the category it ticked. antd only
  // renders the children of an expanded node, so without this the leaves
  // existed in the data and were invisible - which is the whole of what the
  // owner asked to see.
  const [expanded, setExpanded] = useState([]);

  const loadLeaves = useCallback(async (cat) => {
    if (!cat || !loadGroups || leavesBy[cat]) return;
    try {
      const r = await loadGroups({ category: cat, documentIds: documentIds || undefined });
      const byFamily = new Map();
      ((r && r.groups) || []).forEach((g) => {
        if (!g.pf) return;
        if (!byFamily.has(g.pf)) byFamily.set(g.pf, new Set());
        if (g.pt) byFamily.get(g.pf).add(g.pt);
      });
      setLeavesBy((prev) => ({
        ...prev,
        [cat]: [...byFamily.entries()].sort((a, b) => a[0].localeCompare(b[0]))
          .map(([pf, pts]) => ({ pf, pts: [...pts].sort() })),
      }));
    } catch (e) {
      // An unreadable category still shows as a tickable root - ticking it
      // asks again and surfaces the error where the person is looking.
      setLeavesBy((prev) => ({ ...prev, [cat]: [] }));
    }
  }, [loadGroups, documentIds, leavesBy]);

  const treeData = useMemo(() => (categories || []).filter(Boolean).map((c) => {
    const name = typeof c === "string" ? c : (c.name || c.category);
    const count = typeof c === "string" ? 0 : Number(c.count || 0);
    const leaves = leavesBy[name];
    return {
      key: `c|${name}`,
      title: count ? `${name} · ${count.toLocaleString()}` : name,
      // `isLeaf: false` until we know: a category we have not looked inside
      // must still offer to be opened, or the parts would be unreachable.
      isLeaf: Array.isArray(leaves) && !leaves.length,
      children: (leaves || []).map((f) => ({
        key: `f|${name}|${f.pf}`,
        title: f.pf,
        isLeaf: !f.pts.length,
        children: f.pts.map((t) => ({ key: `t|${name}|${f.pf}|${t}`, title: t, isLeaf: true })),
      })),
    };
  }), [categories, leavesBy]);

  // WHAT EACH TICK MEANS, as the server reads it: a category tick is every
  // part under it, a family tick is that family's types, a type tick is one
  // type. The source is the ticked nodes' own category, and a definition has
  // ONE source - so ticks across two categories are refused with the reason
  // rather than quietly using the first.
  const partsOf = useMemo(() => ticks.map((k) => {
    const p = String(k).split("|");
    if (p[0] === "c") return { cat: p[1], category: "", family: "", type: "" };
    if (p[0] === "f") return { cat: p[1], category: "", family: p[2], type: "" };
    return { cat: p[1], category: "", family: p[2], type: p[3] };
  }), [ticks]);
  const sources = useMemo(() => [...new Set(partsOf.map((p) => p.cat))], [partsOf]);
  const tooManySources = sources.length > 1;

  const onCheck = (keys, info) => {
    const key = String((info && info.node && info.node.key) || "");
    const parts = key.split("|");
    const on = !!(info && info.checked);
    setTicks((prev) => (on ? [...new Set([...prev, key])] : prev.filter((k) => k !== key)));
    if (!on) return;
    // The breakdown below follows the LAST tick, so a person sees the parts
    // of the thing they just ticked. It is a preview, not the definition.
    if (parts[0] === "c") {
      pickCategory(parts[1]);
      loadLeaves(parts[1]);
      setExpanded((prev) => (prev.includes(key) ? prev : [...prev, key]));
      return;
    }
    if (parts[0] === "f") { setCategory(parts[1]); setFamily(parts[2]); setType(""); return; }
    if (parts[0] === "t") { setCategory(parts[1]); setFamily(parts[2]); setType(parts[3]); }
  };

  // A NAME THE PERSON CAN SEE BEFORE THEY TYPE ONE, and once they type it is
  // theirs - a suggestion that keeps overwriting what somebody wrote is
  // worse than no suggestion.
  const suggested = useMemo(() => {
    if (!ticks.length) return "";
    const first = partsOf[0];
    const what = ticks.length > 1 ? `${ticks.length} part types` : (first.family || first.type || first.cat);
    return `${what} (from ${first.cat})`;
  }, [ticks, partsOf]);
  useEffect(() => { if (!nameTouched) setName(suggested); }, [suggested, nameTouched]);

  const create = async () => {
    if (!onCreate || !ticks.length || tooManySources) return;
    setCreating(true);
    setErr("");
    try {
      await onCreate({
        name: (name || suggested || "").trim(),
        source: { category: sources[0], family: "", type: "" },
        // The ticks, as the server's `parts` list. A category tick carries no
        // family and no type, which is what "every part under it" means
        // there - the same emptiness the single-triple form always used.
        parts: partsOf.map((p) => ({ category: "", family: p.family, type: p.type })),
        measure: "count",
      });
      setTicks([]);
      setNameTouched(false);
      setName("");
    } catch (e) {
      setErr(e.message || "Could not create that category");
    } finally {
      setCreating(false);
    }
  };


  return (
    <div>
      <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 6, lineHeight: 1.45 }}>
        Count the parts inside a category&apos;s elements - beam clamps in the hangers, nuts in the trapezes -
        and use their lengths, areas and volumes. Tick a tracked category - or open it and tick one part family or type - then pick the parts below.
      </div>
      <Input
        size="small"
        style={{ marginBottom: 6 }}
        placeholder="Name this category - e.g. Beam clamps"
        value={name}
        maxLength={120}
        onChange={(e) => { setNameTouched(true); setName(e.target.value); }}
        disabled={!canEdit}
      />
      {catOptions.length ? (
        <div style={{ maxHeight: 240, overflow: "auto", border: `1px solid ${T.line}`, borderRadius: 4, padding: 4, marginBottom: 6 }}>
          <Tree
            checkable
            checkStrictly
            selectable={false}
            treeData={treeData}
            checkedKeys={{ checked: ticks, halfChecked: [] }}
            onCheck={onCheck}
            expandedKeys={expanded}
            onExpand={(keys, info) => {
              setExpanded(keys);
              const k = String((info && info.node && info.node.key) || "");
              if (k.startsWith("c|")) loadLeaves(k.slice(2));
            }}
            loadData={(node) => {
              const k = String(node.key || "");
              return k.startsWith("c|") ? loadLeaves(k.slice(2)) : Promise.resolve();
            }}
          />
        </div>
      ) : (
        <Alert type="info" showIcon style={{ marginBottom: 6 }}
          message="No tracked categories yet - tick some in Model elements first, and their parts appear here." />
      )}
      {ticks.length ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <div style={{ flex: 1, fontSize: 11.5, color: tooManySources ? "#B45309" : T.muted }}>
            {tooManySources
              // A definition has ONE source category (models/derivedCategory),
              // so this is refused with the reason rather than silently using
              // the first ticked category.
              ? `Ticks in ${sources.length} categories - a custom category comes from one. Untick ${sources.slice(1).join(", ")} or make a second category.`
              : `${ticks.length} tick${ticks.length === 1 ? "" : "s"} in ${sources[0]}`}
          </div>
          <Button size="small" onClick={() => { setTicks([]); setNameTouched(false); }}>Clear ticks</Button>
          <Button
            size="small" type="primary" loading={creating}
            disabled={!canEdit || !onCreate || tooManySources || !(name || suggested).trim()}
            onClick={create}
          >
            Create category
          </Button>
        </div>
      ) : null}
      {err ? (
        <Alert type="warning" showIcon style={{ marginBottom: 6 }}
          message={err}
          action={<Button size="small" onClick={ask}>Try again</Button>} />
      ) : null}
      {!category ? null : loading && !payload ? (
        <div style={{ padding: "10px 0", textAlign: "center" }}><Spin size="small" /></div>
      ) : (
        <>
          <AssemblyGroups
            payload={payload}
            loading={loading}
            sourceCategory={category}
            units={(payload && payload.units) || {}}
            totalsNote={PART_CATEGORY_TOTALS_RULE}
            statusMode={statusMode}
            onPreview={onPreview}
            // NO SECOND CREATE DOOR. The dialog creates from the ticks
            // above; AssemblyGroups is the read-only breakdown of whatever
            // was ticked last, and its own create button would be a second
            // path to the same write.
            onCreate={null}
            startOpen
          />
          {/* SAID, NOT DISCOVERED. A part can sit in two custom
              categories; they are views, not a partition. */}
          {groupCount > 0 ? (
            <div style={{ fontSize: 11, color: T.muted, marginTop: 6, lineHeight: 1.45 }}>
              {PART_CATEGORY_OVERLAP_NOTE}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
